"use client";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { Box, Stack, Typography } from "@mui/material";
import Image from "next/image";
import { FC } from "react";
import { Button } from "../../Button";
import {
  CarouselContent,
  CarouselItem,
  CarouselPagination,
  CarouselWrapper,
} from "../../Carousel";
import { ProductCarouselProps } from "../lib/type";
import { useAnalytics } from "../../pages/StepPage/components/useAnalytics/useAnalytics";

const Bracket = ({ id, ...props }: any) => {
  const paintId = "bracket_paint_" + id;
  return (
    <svg
      width="392"
      height="126"
      viewBox="0 0 392 126"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M196.244 56.3338C254.09 56.3338 307.048 35.2074 348.192 0.0795898L391.699 52.7713C338.823 97.9238 270.711 125.098 196.244 125.098C121.859 125.098 53.6651 97.9238 0.788696 52.7713L44.3771 0.162438C85.4398 35.2074 138.398 56.3338 196.244 56.3338Z"
        fill={`url(#${paintId})`}
      />
      <defs>
        <radialGradient
          id={paintId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(44.5 9.00002) rotate(11.7573) scale(312.469 270.669)"
        >
          <stop offset="0" stopColor="#6DCEF5" />
          <stop offset="0.15" stopColor="#00B2EF" />
          <stop offset="0.333" stopColor="#0082C9" />
          <stop offset="0.5" stopColor="#005AA9" />
          <stop offset="1" stopColor="#001C54" />
        </radialGradient>
      </defs>
    </svg>
  );
};
const utmMapping = {
  utmSource: "utm_source",
  utmMedium: "utm_medium",
  utmCampaign: "utm_campaign",
  utmContent: "utm_content",
  utmTerm: "utm_term",
};
const getActionLink = ({
  href,
  utm,
}: {
  href: string;
  utm: { utmCampaign?: string; utmMedium?: string; utmSource?: string; utmContent?: string; utmTerm?: string };
}) => {
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : (process.env.NEXT_PUBLIC_BASE_URL as string);
  const url = new URL(href, origin);
  const params = url.searchParams;

  Object.entries(utm).forEach(([key, value]) => {
    if (value) params.set(utmMapping[key as keyof typeof utmMapping], value);
  });

  return url.toString();
};

export const ProductCarousel: FC<ProductCarouselProps> = ({
  CarouselProps,
  CarouselContentProps,
  CarouselItemProps,
  options,
}) => {
  const { callAnalyticEvent } = useAnalytics();

  const callAnalytics = ({ eventName, eventValue, title, index }: any) => {
    callAnalyticEvent({ eventName: eventName ?? "main_banner_cta_clicked" }, { banner_id: eventValue, cta_text: title, slide_position: index + 1, page_location: null });
  }

  const visibleOptions = options?.filter(({ hidden }) => !hidden);

  if (!visibleOptions?.length) {
    return <></>;
  }

  return (
    <CarouselWrapper {...CarouselProps} className="h-full overflow-x-hidden overflow-y-visible">
      <CarouselContent
        {...CarouselContentProps}
        wrapperClassName=" relative md:h-auto"
      >
        {visibleOptions?.map(
          (
            {
              title,
              description,
              action: {
                prefetch,
                analytics,
                utm,
                utmCampaign,
                utmMedium,
                utmSource,
                ...action
              },
              ImageProps,
            },
            index,
          ) => (
            <CarouselItem key={title ?? index} {...CarouselItemProps}>
              <Box
                className={
                  "relative h-full md:min-h-[400px] lg:min-h-[480px] 2lg:min-h-[586px] justify-between px-0 py-0 shadow-none flex-center-row max-mdPlus:items-end max-mdPlus:pb-10 max-md:block max-sm:min-h-[460px]"
                }
              >
                <Stack
                  className={
                    "z-10 flex min-w-[292px] max-w-[376px] md:max-w-[470px] flex-col gap-2 max-mdPlus:mx-auto max-mdPlus:my-0 max-mdPlus:items-center max-mdPlus:gap-4 max-mdPlus:text-center max-md:relative max-md:top-[230px] max-md:h-[190px] max-md:justify-between max-md:px-6 md:relative md:mt-0 md:gap-4"
                  }
                >
                  <div className="flex flex-col gap-2">
                    {title && (
                      <Typography
                        variant={"h1"}
                        component={"h1"}
                        className={"text-[45px] max-mdPlus:text-[25px]"}
                        dangerouslySetInnerHTML={{ __html: title }}
                      />
                    )}
                    {description && (
                      <Typography dangerouslySetInnerHTML={{ __html: description }} />
                    )}
                  </div>
                  {!!action && !!action.enabled && (
                    <Button
                      key={title}
                      className={cn("max-w-40", action.className)}
                      onClick={() => !!analytics && callAnalytics({ ...analytics, title: action.title, index })}
                      {...action}
                      href={
                        action.href &&
                        getActionLink({
                          href: action.href,
                          utm: utm || {},
                        })
                      }
                    >
                      {action.title}
                    </Button>
                  )}
                  {/* <CarouselPagination
                    className={"absolute -bottom-4 -right-4 z-20 max-md:hidden"}
                  /> */}
                </Stack>
                <Box
                  className={
                    "bg-bannerBlueGradient absolute top-0 flex h-[480px] w-[110%] min-w-[480px] -translate-y-[30%] select-none items-center justify-center rounded-full max-md:left-1/2 max-md:-translate-x-1/2 md:right-0 md:aspect-square md:h-auto md:w-[65%] lg:w-[75%] md:-translate-y-[37%]"
                  }
                >
                  <div className="pointer-events-none absolute left-0 top-1/3 z-20 h-1/5 w-full -translate-y-[80%] md:-translate-y-[67%] bg-whiteGradient blur-[20px]"></div>

                  <Box
                    className={
                      "relative md:absolute md:bottom-[18%] flex aspect-square w-[45%] items-center justify-center md:w-[56%]"
                    }
                  >
                    <div className="w-full aspect-square rounded-full overflow-hidden relative">
                      <Image
                        src={""}
                        alt={""}
                        className={"object-cover md:translate-y-[10%]"}
                        fill
                        quality={100}
                        priority={index === 0}
                        {...ImageProps}
                      />
                    </div>
                    <Bracket
                      className="absolute bottom-0 h-2/5 w-4/5 translate-y-[31%] md:h-[23%] md:w-[72%]"
                      id={index}
                    />
                  </Box>
                </Box>
                <CarouselPagination
                  className={
                    "absolute bottom-0 left-[30%] z-20 ml-auto -translate-x-1/2 max-mdPlus:mx-auto max-mdPlus:my-0 max-md:hidden md:left-[49%] md:bottom-[20%]"
                  }
                />
              </Box>
            </CarouselItem>
          ),
        )}
      </CarouselContent>
      <CarouselPagination
        className={
          "absolute bottom-0 left-1/2 z-20 ml-auto -translate-x-1/2 max-mdPlus:mx-auto max-mdPlus:my-0 md:bottom-[100px] md:hidden"
        }
      />
    </CarouselWrapper>
  );
};
